import { useState } from 'react';
import { Pagination } from '@quero-delivery/quero-components-web';
import {
  getEndOfDay,
  isSameHour,
  getStartOfDay,
  format,
  toDate,
} from '@querodelivery/qd-date-helper';
import { faSort } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import * as Form from '@radix-ui/react-form';
import { Button, Text, useTheme } from '@querodelivery/uni-react';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';

import styles from './Events.module.scss';
import { useAppDispatch } from '@/_store/hooks';
import type { GetAnalyticsParams } from '@/_features/events/api/get-places-infos-dashboard';
import { useGetAnalytics } from '@/_features/events/api/get-places-infos-dashboard';
import type {
  TypeEventsListForm,
  EventAnalytics,
} from '@/_features/events/types';
import { showEventModal } from '@/_store/modules/layout';
import { EVENTS_FORM_LIST_SCHEMA } from '@/_features/events/constants';
import { useGetAnalyticsEvents } from '@/_features/events/api/get-analytics-events';
import {
  removeSpecialCaracteres,
  stringifyQueryStringArray,
} from '@/Utils/FnUtils';
import { GET_ANALYTICS } from '@/_features/events/api/constants';
import { useCities } from '@/_hooks/use-cities';
import { TableSkeleton } from '@/_components/skeletons/table-skeleton/table-skeleton';
import { TextInput } from '@/_components/text-input/text-input';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { CalendarInput } from '@/_components/calendar-input/calendar-input';
import { Table, useStackedTableLayout } from '@/_components/table';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';

const EVENTS_TABLE_GRID_COLUMNS = '7fr  1.2fr';

function EventsList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isStackedTableLayout = useStackedTableLayout();
  const { color } = useTheme();

  const [sort, setSort] = useState('-1');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  const startDateDefaultValue = searchParams.get('startDate');
  const endDateDefaultValue = searchParams.get('endDate');

  const [params, setParams] = useState<GetAnalyticsParams>({
    endDate: endDateDefaultValue
      ? new Date(endDateDefaultValue)
      : getEndOfDay(toDate()),
    startDate: startDateDefaultValue
      ? new Date(startDateDefaultValue)
      : getStartOfDay(toDate()),
    deliveryId: searchParams.get('deliveryId') || undefined,
    placeId: searchParams.get('placeId') || undefined,
    deliverymanId: searchParams.get('deliverymanId') || undefined,
    cityId: searchParams.get('cityId') || undefined,
    event: searchParams.get('event') || undefined,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TypeEventsListForm>({
    resolver: yupResolver(EVENTS_FORM_LIST_SCHEMA),
    defaultValues: params,
  });

  const [startDate, endDate] = useWatch({
    control,
    name: ['startDate', 'endDate'],
  });

  const [limit] = useState(DEFAULT_LIMIT_REQUEST);

  const { CITIES_OPTIONS, loadingCities } = useCities();

  const { data: eventTypes } = useGetAnalyticsEvents();

  const { data, isLoading, isRefetching } = useGetAnalytics({
    ...params,
    page,
    sort,
  });

  const { data: events = [], totalCount = 0 } = data || {};

  function renderEvent(event: EventAnalytics) {
    return (
      <Table.Row
        key={event._id}
        gridTemplateColumns={EVENTS_TABLE_GRID_COLUMNS}
        onClick={() => dispatch(showEventModal(event))}
      >
        <Table.Cell>
          <Text
            fontWeight="semibold"
            tone="supporting"
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            {event.evento}
          </Text>
        </Table.Cell>

        <Table.Cell>
          <Text>
            {event.createdAt
              ? format(new Date(event.createdAt), 'dd/MM/yyyy HH:mm')
              : null}
          </Text>
        </Table.Cell>
      </Table.Row>
    );
  }

  function renderEventMobileCard(event: EventAnalytics) {
    return (
      <Table.MobileCard
        key={event._id}
        onClick={() => dispatch(showEventModal(event))}
      >
        <Table.MobileItem label="Evento">
          <Text
            fontWeight="semibold"
            tone="supporting"
            style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
          >
            {event.evento}
          </Text>
        </Table.MobileItem>
        <Table.MobileItem label="Criado em">
          {event.createdAt
            ? format(new Date(event.createdAt), 'dd/MM/yyyy HH:mm')
            : null}
        </Table.MobileItem>
      </Table.MobileCard>
    );
  }

  const eventsOptions = eventTypes
    ? eventTypes.map(typeEvent => ({
        key: typeEvent,
        text: typeEvent,
        value: typeEvent,
      }))
    : [];

  function submitForm(data: TypeEventsListForm) {
    const {
      cityId,
      endDate,
      startDate,
      deliveryId,
      deliverymanId,
      event,
      placeId,
    } = data;

    const queries: GetAnalyticsParams = {
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      cityId: cityId || undefined,
      deliveryId: deliveryId || undefined,
      deliverymanId: deliverymanId || undefined,
      event: event || undefined,
      placeId: placeId || undefined,
    };

    queryClient.invalidateQueries([GET_ANALYTICS, queries]);

    setParams(queries);
    setSearchParams(
      stringifyQueryStringArray({
        ...queries,
        startDate: startDate?.toISOString() || '',
        endDate: endDate?.toISOString() || '',
      }),
    );

    setPage(1);
  }

  const isLoadingEvents = isLoading || isRefetching;

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setSearchParams(data => {
      const newSearchParams = new URLSearchParams(data);
      newSearchParams.set('page', String(newPage));
      return newSearchParams;
    });
  };

  return (
    <div className="container">
      <Form.Root className={styles.main} onSubmit={handleSubmit(submitForm)}>
        <div className={styles.line}>
          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange, value } }) => (
              <CalendarInput
                name="startDate"
                label="Data inicial"
                value={value}
                selectsStart
                startDate={value}
                endDate={endDate}
                onChange={(newStartDate: Date | null) => {
                  if (!newStartDate) {
                    return onChange(undefined);
                  }

                  if (isSameHour(newStartDate, getStartOfDay(newStartDate))) {
                    setSearchParams(data => {
                      const newSearchParams = new URLSearchParams(data);
                      newSearchParams.set(
                        'startDate',
                        newStartDate.toISOString(),
                      );
                      return newSearchParams;
                    });
                    return onChange(getStartOfDay(newStartDate));
                  }

                  onChange(newStartDate);
                }}
                maxDate={new Date()}
                todayButton="Hoje"
                timeInputLabel="Horário:"
                showTimeInput
                popperPlacement="top"
              />
            )}
          />

          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange, value } }) => (
              <CalendarInput
                name="endDate"
                label="Data final"
                value={value}
                selectsStart
                startDate={value}
                endDate={value}
                onChange={(newEndDate: Date | null) => {
                  if (!newEndDate) {
                    return onChange(undefined);
                  }

                  if (isSameHour(newEndDate, getStartOfDay(newEndDate))) {
                    setSearchParams(data => {
                      const newSearchParams = new URLSearchParams(data);
                      newSearchParams.set('endDate', newEndDate.toISOString());
                      return newSearchParams;
                    });
                    return onChange(getEndOfDay(newEndDate));
                  }

                  onChange(newEndDate);
                }}
                maxDate={new Date()}
                minDate={startDate || undefined}
                todayButton="Hoje"
                timeInputLabel="Horário:"
                showTimeInput
                popperPlacement="top"
              />
            )}
          />

          <Controller
            control={control}
            name="deliverymanId"
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="deliverymanId"
                label="Entregador ID"
                placeholder="ID do entregador"
                value={value}
                errorMessage={errors.deliverymanId?.message}
                onChange={event => {
                  const handlerData = removeSpecialCaracteres(
                    event.target.value,
                  );
                  onChange(handlerData);
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="deliveryId"
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="deliveryId"
                label="Entrega ID"
                placeholder="ID da entrega"
                value={value}
                errorMessage={errors.deliveryId?.message}
                onChange={event => {
                  const handlerData = removeSpecialCaracteres(
                    event.target.value,
                  );
                  onChange(handlerData);
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="placeId"
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="placeId"
                placeholder="ID do estabelecimento"
                label="Estabelecimento ID"
                value={value}
                errorMessage={errors.placeId?.message}
                onChange={event => {
                  const handlerData = removeSpecialCaracteres(
                    event.target.value,
                  );
                  onChange(handlerData);
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="cityId"
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                label="Município"
                options={CITIES_OPTIONS}
                loading={loadingCities}
                value={value}
                search
                placeholder="Selecione o município"
                onChange={onChange}
              />
            )}
          />
        </div>

        <div className={styles.eventLine}>
          <div className={styles.eventoWrapper}>
            <Controller
              control={control}
              name="event"
              render={({ field: { onChange, value } }) => (
                <DropdownInput
                  label="Evento"
                  placeholder="Selecione o evento"
                  value={value}
                  search
                  clearable
                  onChange={onChange}
                  options={eventsOptions}
                />
              )}
            />
          </div>
          <div className={styles.containerButton}>
            <Button
              type="submit"
              variant="cta"
              size="lg"
              disabled={isLoading}
              loading={isLoading}
              width="100%"
            >
              buscar
            </Button>
          </div>
        </div>
      </Form.Root>

      <div className="mgn-top-sm">
        {isLoadingEvents ? (
          <TableSkeleton columnCount={3} rowCount={10} rowHeight={31} />
        ) : events && events.length > 0 ? (
          <>
            <Text
              variant="headingMd"
              tone="supporting"
              className={styles.headerLabel}
            >
              {totalCount} {totalCount === 1 ? 'evento' : 'eventos'}
            </Text>
            {isStackedTableLayout ? (
              <Table.MobileList className={styles.eventsTable}>
                {events.map(event => renderEventMobileCard(event))}
              </Table.MobileList>
            ) : (
              <Table className={styles.eventsTable}>
                <Table.Header>
                  <Table.Row gridTemplateColumns={EVENTS_TABLE_GRID_COLUMNS}>
                    <Table.HeaderCell>Evento</Table.HeaderCell>
                    <Table.HeaderCell>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        Criado em
                        <div
                          className="li-arrow"
                          style={{
                            marginLeft: '1rem',
                          }}
                          onClick={() => {
                            if (sort === '1') {
                              setSort('-1');
                            } else {
                              setSort('1');
                            }
                          }}
                        >
                          <FontAwesomeIcon
                            style={{
                              cursor: 'pointer',
                              rotate: sort === '1' ? '0deg' : '180deg',
                            }}
                            icon={faSort}
                            size="1x"
                            color={color['color-text-secondary']}
                          />
                        </div>
                      </div>
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {events!.map(event => renderEvent(event))}
                </Table.Body>
              </Table>
            )}
            <div className={styles.pagination}>
              <Pagination
                size="sm"
                limitPerPage={limit}
                currentPage={page}
                totalCount={totalCount || 0}
                onPageChange={handleChangePage}
              />
            </div>
          </>
        ) : (
          <TableEmptyState
            title="Nenhum evento encotrado"
            description="Não encontramos eventos com os filtros selecionados."
          />
        )}
      </div>
    </div>
  );
}

export default EventsList;
